import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatUnits, isAddress, parseUnits } from 'viem';
import { toast } from 'react-toastify';
import { environment } from '@/config/environment';
import { Spinner } from '@/components/Spinner';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Icon, ICONS } from '@/components/brand';
import { parseTxError } from '@/utils/txError';
import {
  entryNameByteLength,
  firstPayoutOnOrAfter,
  payoutAfter,
  MAX_ENTRY_NAME_BYTES,
  MAX_PERIODS,
  MAX_START_LOOKAHEAD,
  MIN_START_TIME,
  PAGE_SIZES,
  useDistributor,
  type DistributorEntry,
} from '@/hooks/useDistributor';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const toSeconds = (value: bigint | number) => (typeof value === 'bigint' ? Number(value) : value);

/** The exact instant, as the chain states it. Shown wherever precision matters. */
const date = (seconds: bigint | number, unset = 'None') => {
  const value = toSeconds(seconds);
  if (!value) return unset;
  return `${new Date(value * 1000).toISOString().replace('T', ' ').slice(0, 16)} UTC`;
};

/**
 * The same instant in Asia/Shanghai. Shown beside every payout date because the
 * schedule was agreed in that timezone -- "12:00 UTC" is the chain's statement
 * of the same moment people were told as 20:00. A fixed +08:00 offset rather
 * than a timezone database, because China observes no daylight saving.
 */
const shanghai = (seconds: bigint | number) => {
  const value = toSeconds(seconds);
  if (!value) return '';
  return `${new Date((value + 8 * 3600) * 1000).toISOString().replace('T', ' ').slice(0, 16)} +08`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/**
 * "26 Sep 2026" -- the human reading, in UTC so it never disagrees with `date`.
 * Spelled out by hand rather than through toLocaleDateString, whose en-GB short
 * month for September is "Sept" and whose output varies by runtime ICU data.
 */
const dayMonthYear = (seconds: bigint | number) => {
  const value = toSeconds(seconds);
  if (!value) return '—';
  const d = new Date(value * 1000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};
const monthYear = (seconds: bigint | number) => {
  const d = new Date(toSeconds(seconds) * 1000);
  return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const relative = (seconds: bigint | number) => {
  const diff = toSeconds(seconds) - Date.now() / 1000;
  if (diff <= 0) return 'now';
  const hours = diff / 3600;
  if (hours < 1) return `in ${Math.max(1, Math.round(diff / 60))} min`;
  if (hours < 36) return `in ${Math.round(hours)} h`;
  return `in ${Math.round(hours / 24)} days`;
};

const group = (whole: string) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Every figure on this screen, as displayed: grouped, and at most TWO decimals.
 *
 * Truncated, not rounded, so a displayed figure never overstates what exists or
 * what is owed -- the convention wallets use for balances. The one case that
 * would mislead is a real but tiny amount truncating to "0" next to a "Payment
 * due" pill, so anything above zero and below 0.01 reads "<0.01" instead.
 *
 * The exact figure is never lost: see `exact`, which the call sites put in a
 * hover title, and anything that is TYPED into an input (the "Use …" fill, the
 * Top up prefill) carries full precision so the chain is asked for the real
 * amount rather than a rounded one.
 */
const display = (value: bigint, decimals: number) => {
  const [whole, fraction = ''] = formatUnits(value, decimals).split('.');
  const cents = fraction.slice(0, 2).replace(/0+$/, '');
  if (value > 0n && whole === '0' && cents === '') return '<0.01';
  return `${group(whole)}${cents ? `.${cents}` : ''}`;
};
const amount = (value: bigint) => display(value, environment.tokenDecimals);
const tokens = (value: bigint) => `${amount(value)} SAHARA`;

/** The full-precision figure, for hover titles. */
const exact = (value: bigint) => {
  const [whole, fraction] = formatUnits(value, environment.tokenDecimals).split('.');
  return `${group(whole)}${fraction ? `.${fraction}` : ''} SAHARA`;
};

const shortAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`;

const EXPLORERS: Record<number, string> = {
  1: 'https://etherscan.io',
  84532: 'https://sepolia.basescan.org',
};
const EXPLORER_NAMES: Record<number, string> = {
  1: 'Etherscan',
  84532: 'Basescan',
};
const explorerAddress = (address: string) => {
  const base = EXPLORERS[environment.chainId];
  return base ? `${base}/address/${address}` : null;
};

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Address copied');
  } catch {
    toast.error('Could not copy the address.');
  }
};

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

/**
 * What a set of form values will actually produce, restated from the contract's
 * rules so the operator sees real dates and real amounts before signing.
 *
 * Deliberately computed the same way the contract does -- `total * k / periods`
 * differences rather than a stored per-period constant -- so every figure shown
 * here carries the rounding exactly as the chain will. A preview that divided
 * evenly would be wrong by a few wei in the one place it matters.
 */
export function previewSchedule(
  totalWei: bigint,
  startTime: number,
  frontLoaded: boolean,
  periods: number,
): { first: number; last: number; dateCount: number; rows: { when: number; amount: bigint }[] } {
  const first = firstPayoutOnOrAfter(startTime);
  const dateCount = frontLoaded ? periods + 1 : periods;
  const cumulative = (elapsed: number): bigint => {
    if (elapsed <= 0) return 0n;
    if (frontLoaded) {
      if (elapsed > periods) return totalWei;
      const front = (totalWei * 2500n) / 10000n;
      return front + ((totalWei - front) * BigInt(elapsed - 1)) / BigInt(periods);
    }
    if (elapsed >= periods) return totalWei;
    return (totalWei * BigInt(elapsed)) / BigInt(periods);
  };
  const rows: { when: number; amount: bigint }[] = [];
  for (let k = 0; k < dateCount; k++) {
    rows.push({ when: payoutAfter(first, k), amount: cumulative(k + 1) - cumulative(k) });
  }
  return { first, last: payoutAfter(first, dateCount - 1), dateCount, rows };
}

type DateStatus = 'paid' | 'due' | 'upcoming' | 'lost';

/**
 * Each payout date of a live entry, with where it stands.
 *
 * A date is PAID when everything through it has gone out -- read off the
 * cumulative figure against `paidAmount`, because payments are not stored per
 * date and a single late run settles several at once. It is DUE when it has
 * arrived and is not yet covered, and LOST once the entry is cancelled, which
 * forfeits every date not already paid.
 */
function entrySchedule(entry: DistributorEntry) {
  const { rows } = previewSchedule(
    entry.totalAmount,
    Number(entry.firstPaymentTime),
    entry.frontLoaded,
    entry.periods,
  );
  let cumulative = 0n;
  return rows.map((row, k) => {
    cumulative += row.amount;
    const status: DateStatus =
      cumulative <= entry.paidAmount
        ? 'paid'
        : entry.cancelled
          ? 'lost'
          : BigInt(k) < entry.payoutsElapsed
            ? 'due'
            : 'upcoming';
    return { ...row, index: k, status };
  });
}

/** One segment per date; long schedules fall back to a continuous bar. */
const Timeline: React.FC<{ statuses: DateStatus[]; paidRatio: number }> = ({ statuses, paidRatio }) =>
  statuses.length <= 60 ? (
    <div className="pr-segs" aria-hidden="true">
      {statuses.map((status, i) => (
        <span key={i} className={`pr-seg is-${status}`} />
      ))}
    </div>
  ) : (
    <div className="pr-meter" aria-hidden="true">
      <span style={{ width: `${Math.min(100, paidRatio * 100)}%` }} />
    </div>
  );

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

/** Runs one async action at a time and reports it through toasts. */
const useAction = () => {
  const [busy, setBusy] = useState<string | null>(null);
  const run = async (key: string, success: string, action: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    try {
      await action();
      // An empty message means the action reports its own outcome (a count
      // only it knows), so no generic toast on top of it.
      if (success) toast.success(success);
    } catch (err) {
      const parsed = parseTxError(err, 'Action failed.');
      // A wallet rejection is the user's own decision, not a failure to report
      // as an error -- the same distinction AdminPanel draws.
      if (parsed.kind === 'rejected') toast.info(parsed.message);
      else toast.error(parsed.message);
    } finally {
      setBusy(null);
    }
  };
  return { busy, run };
};

type Run = ReturnType<typeof useAction>['run'];

const Fact: React.FC<{ label: string; children: React.ReactNode; hint?: React.ReactNode }> = ({
  label, children, hint,
}) => (
  <div>
    <dt className="sh-label">{label}</dt>
    <dd>{children}</dd>
    {hint && <p className="pr-help" style={{ marginTop: 4 }}>{hint}</p>}
  </div>
);

const Stat: React.FC<{ label: string; value: bigint; tone?: string; hint?: string }> = ({
  label, value, tone, hint,
}) => (
  <div>
    <div className="sh-label">{label}</div>
    <div className="pr-stat-value" style={tone ? { color: tone } : undefined} title={exact(value)}>
      {amount(value)}
      <span className="pr-unit">SAHARA</span>
    </div>
    {hint && <p className="pr-help" style={{ marginTop: 6 }}>{hint}</p>}
  </div>
);

const StatusPills: React.FC<{ entry: DistributorEntry }> = ({ entry }) => {
  if (entry.cancelled) return <span className="sh-pill sh-pill-danger">Cancelled</span>;
  const finished = entry.payoutsElapsed >= entry.payoutCount && entry.owed === 0n;
  return (
    <>
      {entry.owed > 0n && <span className="sh-pill sh-pill-warn">Payment due</span>}
      {/* No "Active" pill: it would sit on nearly every row and say nothing. A
          row earns a pill only for something the operator should notice. */}
      {finished && <span className="sh-pill sh-pill-info">Complete</span>}
      {entry.frontLoaded && <span className="sh-pill">25% up front</span>}
      {entry.paidAmount > entry.totalAmount && (
        <span className="sh-pill sh-pill-warn">Overpaid vs. current total</span>
      )}
    </>
  );
};

const AddressLine: React.FC<{ address: string }> = ({ address }) => {
  const href = explorerAddress(address);
  return (
    <span className="pr-addr">
      <span title={address}>{shortAddress(address)}</span>
      <button type="button" className="pr-icon-btn" aria-label="Copy address" onClick={() => void copy(address)}>
        <Icon d={ICONS.copy} size={12} />
      </button>
      {href && (
        <a className="pr-icon-btn" href={href} target="_blank" rel="noreferrer" aria-label="View on explorer">
          <Icon d={ICONS.external} size={12} />
        </a>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

interface NewPaymentInput {
  name: string;
  recipient: string;
  totalAmountWei: bigint;
  startTime: number;
  frontLoaded: boolean;
  periods: number;
}

const CreateEntryForm: React.FC<{
  onCreate: (input: NewPaymentInput) => Promise<void>;
  onClose: () => void;
  busy: boolean;
}> = ({ onCreate, onClose, busy }) => {
  const [name, setName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [total, setTotal] = useState('');
  const [startDate, setStartDate] = useState('');
  const [frontLoaded, setFrontLoaded] = useState(false);
  const [periods, setPeriods] = useState('12');

  const nameBytes = entryNameByteLength(name);
  const periodCount = Number(periods);
  const startSeconds = startDate ? Math.floor(Date.parse(`${startDate}T00:00:00Z`) / 1000) : NaN;

  let amountWei: bigint | null = null;
  let amountError: string | null = null;
  if (total.trim() !== '') {
    try {
      amountWei = parseUnits(total.trim(), environment.tokenDecimals);
      if (amountWei <= 0n) {
        amountWei = null;
        amountError = 'The total must be above zero.';
      }
    } catch {
      amountError = `Not a number, or more than ${environment.tokenDecimals} decimal places.`;
    }
  }

  const now = Math.floor(Date.now() / 1000);
  const errors: string[] = [];
  if (nameBytes === 0) errors.push('A name is required — it is what identifies this payment.');
  if (nameBytes > MAX_ENTRY_NAME_BYTES) {
    errors.push(`Name is ${nameBytes} UTF-8 bytes; the limit is ${MAX_ENTRY_NAME_BYTES}.`);
  }
  if (!isAddress(recipient.trim())) errors.push('Enter a valid recipient address.');
  if (amountError) errors.push(amountError);
  if (amountWei === null && !amountError) errors.push('Enter the total amount.');
  if (!Number.isFinite(startSeconds)) errors.push('Choose a start date.');
  else if (startSeconds < MIN_START_TIME) errors.push('Start date must be 2022-01-01 or later.');
  else if (startSeconds > now + MAX_START_LOOKAHEAD) {
    errors.push(`Start date cannot be more than ${MAX_START_LOOKAHEAD / 86400} days ahead.`);
  }
  if (!Number.isInteger(periodCount) || periodCount < 1 || periodCount > MAX_PERIODS) {
    errors.push(`Periods must be a whole number between 1 and ${MAX_PERIODS}.`);
  }

  const preview =
    errors.length === 0 && amountWei !== null
      ? previewSchedule(amountWei, startSeconds, frontLoaded, periodCount)
      : null;
  const touched = name + recipient + total + startDate !== '';
  const dateCount = frontLoaded ? periodCount + 1 : periodCount;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (errors.length > 0 || amountWei === null) return;
    await onCreate({
      name: name.trim(),
      recipient: recipient.trim(),
      totalAmountWei: amountWei,
      startTime: startSeconds,
      frontLoaded,
      periods: periodCount,
    });
  };

  const rows = preview?.rows ?? [];
  const middle = rows.length > 1 ? rows[1].amount : null;

  return (
    <section className="pr-create" aria-label="Add a payment">
      <form onSubmit={submit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <div className="sh-label">New payment</div>
            <h2 className="sh-h2" style={{ marginTop: 6 }}>Add a payment</h2>
          </div>
          <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={onClose} aria-label="Close">
            <Icon d={ICONS.close} size={14} />
          </button>
        </div>

        <div className="pr-fields">
          <div className="sh-field is-wide">
            <label htmlFor="payroll-name">Name</label>
            <input id="payroll-name" type="text" className="sh-input" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Advisor retainer — A" />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <p className="pr-help">One wallet may hold several payments — the name says which is which.</p>
              <span className="pr-exact" style={{ color: nameBytes > MAX_ENTRY_NAME_BYTES ? 'var(--danger)' : undefined }}>
                {nameBytes}/{MAX_ENTRY_NAME_BYTES} bytes
              </span>
            </div>
          </div>

          <div className="sh-field is-wide">
            <label htmlFor="payroll-recipient">Recipient</label>
            <input id="payroll-recipient" type="text" className="sh-input sh-mono" value={recipient}
              onChange={(e) => setRecipient(e.target.value)} placeholder="0x…" />
          </div>

          <div className="sh-field">
            <label htmlFor="payroll-amount">Total</label>
            <div className="pr-suffix">
              <input id="payroll-amount" type="text" inputMode="decimal" className="sh-input" value={total}
                onChange={(e) => setTotal(e.target.value)} placeholder="120,000" />
              <span>SAHARA</span>
            </div>
          </div>

          <div className="sh-field">
            <label htmlFor="payroll-start">Start date (UTC)</label>
            <input id="payroll-start" type="date" className="sh-input" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div className="sh-field is-wide">
            <label id="payroll-first-label">First payment</label>
            <div className="pr-choices" role="radiogroup" aria-labelledby="payroll-first-label">
              {([
                [false, 'Even', 'Every payment is the same share of the total.'],
                [true, '25% up front', 'A quarter on the first date, the rest spread over the periods.'],
              ] as const).map(([value, title, sub]) => (
                <button key={title} type="button" role="radio" aria-checked={frontLoaded === value}
                  className={`pr-choice${frontLoaded === value ? ' is-on' : ''}`}
                  onClick={() => setFrontLoaded(value)}>
                  <span className="pr-radio" aria-hidden="true" />
                  <span>
                    <span className="pr-choice-title">{title}</span>
                    <span className="pr-choice-sub">{sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="sh-field">
            <label htmlFor="payroll-periods">{frontLoaded ? 'Periods after the 25%' : 'Periods'}</label>
            <div className="pr-suffix">
              <input id="payroll-periods" type="number" min={1} max={MAX_PERIODS} className="sh-input"
                value={periods} onChange={(e) => setPeriods(e.target.value)} />
              <span>months</span>
            </div>
          </div>
        </div>

        <p className="pr-help" style={{ marginTop: 16 }}>
          The first payment is the next 26th at 12:00 UTC on or after the start date.{' '}
          {Number.isInteger(periodCount) && periodCount > 0 && (
            <strong>{dateCount} payment date{dateCount === 1 ? '' : 's'} in total.</strong>
          )}
        </p>

        {errors.length > 0 && touched && (
          <div role="alert" className="sh-banner sh-banner-warn" style={{ marginTop: 18 }}>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((message) => <li key={message}>{message}</li>)}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button type="submit" className="sh-btn sh-btn-primary" disabled={busy || errors.length > 0}>
            {busy ? 'Creating…' : 'Create payment'}
          </button>
          <button type="button" className="sh-btn" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </form>

      <aside aria-label="Schedule preview">
        <div className="sh-label">Schedule preview</div>
        {!preview ? (
          <p className="pr-help" style={{ marginTop: 14 }}>
            Fill in the total, start date and periods to see every payment date and amount before
            you sign.
          </p>
        ) : (
          <>
            <div style={{ marginTop: 18 }}>
              <div className="pr-big">
                {amount(amountWei!)}
                <span className="pr-unit">SAHARA</span>
              </div>
              <p className="pr-help" style={{ marginTop: 8 }}>
                over {preview.dateCount} month{preview.dateCount === 1 ? '' : 's'}, {dayMonthYear(preview.first)} → {dayMonthYear(preview.last)}
              </p>
            </div>
            <div style={{ marginTop: 18 }}>
              <Timeline statuses={preview.rows.map(() => 'upcoming' as DateStatus)} paidRatio={0} />
            </div>
            <dl className="pr-facts" style={{ marginTop: 22 }}>
              <Fact label="First payment" hint={shanghai(preview.first)}>{date(preview.first)}</Fact>
              <Fact label="Last payment" hint={shanghai(preview.last)}>{date(preview.last)}</Fact>
              <Fact label="First amount"><span title={exact(preview.rows[0].amount)}>{amount(preview.rows[0].amount)} SAHARA</span></Fact>
              <Fact label="Then each month">{middle !== null ? <span title={exact(middle)}>{amount(middle)} SAHARA</span> : '—'}</Fact>
            </dl>
            <p className="pr-help" style={{ marginTop: 16 }}>
              Amounts are shown to two decimals; hover any figure for the exact value. The last
              payment absorbs any rounding, so the payments sum to the total exactly.
            </p>
          </>
        )}
      </aside>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Claim one payment
// ---------------------------------------------------------------------------

/**
 * Collect a single payment, from its own panel.
 *
 * Always shown to a wallet allowed to collect it -- a claimer, the owner, or the
 * recipient themselves -- even when nothing is due, so the action is findable on
 * any day of the month rather than only on the 26th. When it cannot run, the
 * block says exactly why and when it next can, instead of vanishing.
 */
const ClaimBlock: React.FC<{
  entry: DistributorEntry;
  canPay: boolean;
  paused: boolean;
  pool: bigint;
  busy: string | null;
  run: Run;
  distributor: ReturnType<typeof useDistributor>;
}> = ({ entry, canPay, paused, pool, busy, run, distributor }) => {
  const due = entry.owed > 0n;
  const payable = due && pool > 0n && !paused;
  const covered = entry.owed <= pool ? entry.owed : pool;
  const reason = paused
    ? 'Payouts are paused. Nothing is forfeited; this can be claimed once they resume.'
    : !due
      ? entry.nextPaymentTime > 0n
        ? `Nothing due yet. The next ${tokens(entry.nextPaymentAmount)} becomes claimable on ${dayMonthYear(entry.nextPaymentTime)} at 12:00 UTC.`
        : 'Everything this payment will ever pay has been paid.'
      : pool === 0n
        ? 'The pool is empty. Top it up, then claim.'
        : null;

  return (
    <div className={`pr-claim${due ? ' is-due' : ''}`} aria-label="Claim this payment">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div className="sh-label">{canPay ? 'Claim for the recipient' : 'Claim'}</div>
        {due && <span className="sh-pill sh-pill-warn">Due now</span>}
      </div>
      <div className="pr-big" title={exact(entry.owed)} style={{ marginTop: 14, color: due ? 'var(--accent)' : undefined }}>
        {amount(entry.owed)}<span className="pr-unit">SAHARA</span>
      </div>
      <p className="pr-help" style={{ marginTop: 8 }}>
        Paid to <span className="sh-mono">{shortAddress(entry.recipient)}</span>, the recorded
        recipient — whoever presses the button.
      </p>
      {due && covered < entry.owed && pool > 0n && (
        <p className="pr-help" style={{ marginTop: 6, color: 'var(--warn)' }}>
          The pool holds {tokens(pool)}, so this pays {tokens(covered)} and records the rest as still owed.
        </p>
      )}
      <button type="button"
        className={`sh-btn ${payable ? 'sh-btn-primary' : ''}`}
        style={{ marginTop: 14 }}
        disabled={!!busy || !payable}
        onClick={() => run(`claim-${entry.id}`, 'Payment sent.', () => distributor.claimEntries([entry.id]))}>
        {busy === `claim-${entry.id}` ? 'Claiming…' : payable ? `Claim ${tokens(covered)}` : 'Nothing to claim'}
      </button>
      {reason && <p className="pr-help" style={{ marginTop: 10 }}>{reason}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Expanded payment: its schedule and the owner's controls over it
// ---------------------------------------------------------------------------

const EntryDetails: React.FC<{
  entry: DistributorEntry;
  isOwner: boolean;
  canCollect: boolean;
  canPay: boolean;
  paused: boolean;
  pool: bigint;
  busy: string | null;
  run: Run;
  distributor: ReturnType<typeof useDistributor>;
}> = ({ entry, isOwner, canCollect, canPay, paused, pool, busy, run, distributor }) => {
  const [recipient, setRecipient] = useState('');
  const [total, setTotal] = useState('');
  const [name, setName] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);

  const id = entry.id;
  const remaining = entry.totalAmount > entry.paidAmount ? entry.totalAmount - entry.paidAmount : 0n;
  const schedule = useMemo(() => entrySchedule(entry), [entry]);
  const nextIndex = schedule.findIndex((row) => row.status === 'upcoming');
  const nameBytes = entryNameByteLength(name.trim());

  return (
    <div className="pr-detail">
      <div>
        <dl className="pr-facts">
          <Fact label="Total">{tokens(entry.totalAmount)}</Fact>
          <Fact label="Paid so far">{tokens(entry.paidAmount)}</Fact>
          <Fact
            label="Remaining"
            hint={
              entry.paidAmount > entry.totalAmount
                ? 'Already paid more than the current total — the total was lowered after payments went out. Nothing further is owed.'
                : undefined
            }
          >
            {tokens(remaining)}
          </Fact>
          <Fact label="Owed now">
            <span style={{ color: entry.owed > 0n ? 'var(--accent)' : undefined }}>{tokens(entry.owed)}</span>
          </Fact>
          <Fact label="Shape">
            {entry.frontLoaded
              ? `25% on the first date, then ${entry.periods} monthly payments`
              : `${entry.periods} equal monthly payments`}
          </Fact>
          <Fact label="Recipient"><AddressLine address={entry.recipient} /></Fact>
        </dl>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 26, gap: 12, flexWrap: 'wrap' }}>
          <div className="sh-label">Schedule · entry #{id.toString()}</div>
          <div className="pr-legend">
            <span><i className="is-paid" />Paid</span>
            <span><i className="is-due" />Due</span>
            <span><i />Upcoming</span>
            {entry.cancelled && <span><i className="is-lost" />Forfeited</span>}
          </div>
        </div>
        <div className="pr-sched">
          <table>
            <thead>
              <tr><th>#</th><th>Date (UTC)</th><th className="num">Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {schedule.map((row) => (
                <tr key={row.index} className={row.index === nextIndex && !entry.cancelled ? 'is-next' : undefined}>
                  <td className="pr-exact">{row.index + 1}</td>
                  <td>{dayMonthYear(row.when)} <span className="pr-exact">12:00</span></td>
                  <td className="num" title={exact(row.amount)}>{amount(row.amount)}</td>
                  <td>
                    <span className={`pr-status is-${row.status}`}>
                      {row.status === 'paid' ? 'Paid' : row.status === 'due' ? 'Due' : row.status === 'lost' ? 'Forfeited' : row.index === nextIndex ? 'Next' : 'Upcoming'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        {canCollect && !entry.cancelled && (
          <ClaimBlock entry={entry} canPay={canPay} paused={paused} pool={pool} busy={busy} run={run}
            distributor={distributor} />
        )}
        {!isOwner || entry.cancelled ? (
          <div>
            <div className="sh-label">{entry.cancelled ? 'Cancelled' : 'Next payment'}</div>
            {entry.cancelled ? (
              <p className="pr-help" style={{ marginTop: 12 }}>
                This payment was cancelled. Everything it had not already paid was forfeited; the{' '}
                {tokens(entry.paidAmount)} already paid is unaffected.
              </p>
            ) : entry.nextPaymentTime > 0n ? (
              <>
                <div className="pr-big" style={{ marginTop: 14 }}>
                  {amount(entry.nextPaymentAmount)}<span className="pr-unit">SAHARA</span>
                </div>
                <p className="pr-help" style={{ marginTop: 8 }}>
                  {date(entry.nextPaymentTime)} · {shanghai(entry.nextPaymentTime)}
                </p>
              </>
            ) : (
              <p className="pr-help" style={{ marginTop: 12 }}>Schedule complete.</p>
            )}
          </div>
        ) : (
          <>
            <div className="sh-label" style={{ marginBottom: 18 }}>Manage</div>

            <div className="pr-control">
              <div className="pr-control-title">Move to another wallet</div>
              <p className="pr-help">Everything already paid stays where it went.</p>
              <div className="pr-inline">
                <input id={`recipient-${id}`} type="text" className="sh-input sh-mono" placeholder="0x…"
                  aria-label="New recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
                <button type="button" className="sh-btn sh-btn-sm" disabled={!!busy || !isAddress(recipient.trim())}
                  onClick={() => run(`recipient-${id}`, 'Recipient updated.', async () => {
                    await distributor.setRecipient(id, recipient.trim());
                    setRecipient('');
                  })}>
                  Move payment
                </button>
              </div>
            </div>

            <div className="pr-control">
              <div className="pr-control-title">Change the total</div>
              <p className="pr-help">
                The whole schedule is re-priced, past dates included, so the difference lands in the
                next payment.
              </p>
              <div className="pr-inline">
                <div className="pr-suffix">
                  <input id={`total-${id}`} type="text" inputMode="decimal" className="sh-input"
                    aria-label="New total" placeholder={amount(entry.totalAmount)}
                    value={total} onChange={(e) => setTotal(e.target.value)} />
                  <span>SAHARA</span>
                </div>
                <button type="button" className="sh-btn sh-btn-sm" disabled={!!busy || total.trim() === ''}
                  onClick={() => run(`total-${id}`, 'Total updated.', async () => {
                    await distributor.setTotalAmount(id, parseUnits(total.trim(), environment.tokenDecimals));
                    setTotal('');
                  })}>
                  Update total
                </button>
              </div>
            </div>

            <div className="pr-control">
              <div className="pr-control-title">Rename</div>
              <div className="pr-inline">
                <input id={`name-${id}`} type="text" className="sh-input" placeholder={entry.name}
                  aria-label="New name" value={name} onChange={(e) => setName(e.target.value)} />
                <button type="button" className="sh-btn sh-btn-sm"
                  disabled={!!busy || nameBytes === 0 || nameBytes > MAX_ENTRY_NAME_BYTES}
                  onClick={() => run(`name-${id}`, 'Name updated.', async () => {
                    await distributor.setName(id, name.trim());
                    setName('');
                  })}>
                  Rename
                </button>
              </div>
            </div>

            <div className="pr-control">
              <div className="pr-control-title" style={{ color: 'var(--danger)' }}>Cancel this payment</div>
              <p className="pr-help">
                Terminal, and a full clawback: it forfeits everything not already paid, including
                amounts that have already come due and were never collected.
              </p>
              <button type="button" className="sh-btn sh-btn-sm sh-btn-danger" style={{ marginTop: 12 }}
                disabled={!!busy} onClick={() => setConfirmCancel(true)}>
                Cancel payment
              </button>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmCancel}
        destructive
        title={`Cancel “${entry.name}”?`}
        message={
          `This is terminal and cannot be undone. ${tokens(entry.owed)} has already come due and ` +
          `has not been collected — cancelling forfeits that too, along with the ` +
          `${tokens(remaining)} still to come. The ${tokens(entry.paidAmount)} already paid is not ` +
          'affected. The tokens stay in the pool and fund the rest of the roster.'
        }
        confirmText="Cancel this payment"
        cancelText="Keep it"
        isLoading={busy === `cancel-${id}`}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() =>
          run(`cancel-${id}`, 'Payment cancelled.', async () => {
            await distributor.cancelEntry(id);
            setConfirmCancel(false);
          })
        }
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Roster row
// ---------------------------------------------------------------------------

const PaymentRow: React.FC<{
  entry: DistributorEntry;
  open: boolean;
  canPay: boolean;
  paused: boolean;
  busy: string | null;
  run: Run;
  distributor: ReturnType<typeof useDistributor>;
  isOwner: boolean;
  account: string | null;
  pool: bigint;
}> = ({ entry, open, canPay, paused, busy, run, distributor, isOwner, account, pool }) => {
  const schedule = useMemo(() => entrySchedule(entry), [entry]);
  // The contract lets a recipient collect their own entries whoever else holds
  // a role, so the button follows that rule rather than `canPay` alone.
  const canCollect = canPay || (!!account && account.toLowerCase() === entry.recipient.toLowerCase());
  const paidRatio = entry.totalAmount > 0n ? Number((entry.paidAmount * 1000n) / entry.totalAmount) / 1000 : 0;
  const href = open ? '/payroll' : `/payroll/entry/${entry.id}`;

  return (
    <article className={`pr-item${open ? ' is-open' : ''}${entry.cancelled ? ' is-cancelled' : ''}`}>
      <div className="pr-row">
        <div style={{ minWidth: 0 }}>
          <Link to={href} replace className="pr-name">{entry.name}</Link>
          <div><AddressLine address={entry.recipient} /></div>
          {(entry.cancelled || entry.owed > 0n || entry.frontLoaded || entry.paidAmount > entry.totalAmount ||
            (entry.payoutsElapsed >= entry.payoutCount && entry.owed === 0n)) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              <StatusPills entry={entry} />
            </div>
          )}
        </div>

        <div style={{ minWidth: 0 }}>
          <Timeline statuses={schedule.map((row) => row.status)} paidRatio={paidRatio} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 10, fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-2)' }}>
              {amount(entry.paidAmount)} <span style={{ color: 'var(--text-muted)' }}>of {tokens(entry.totalAmount)}</span>
            </span>
            <span className="pr-exact">{entry.payoutsElapsed.toString()}/{entry.payoutCount.toString()} dates</span>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          {entry.cancelled ? (
            <span className="pr-help">—</span>
          ) : entry.nextPaymentTime > 0n ? (
            <>
              <div style={{ fontSize: 13.5 }} title={`${date(entry.nextPaymentTime)} · ${shanghai(entry.nextPaymentTime)}`}>
                {dayMonthYear(entry.nextPaymentTime)}
              </div>
              <div className="pr-exact" style={{ marginTop: 4 }} title={exact(entry.nextPaymentAmount)}>+{amount(entry.nextPaymentAmount)} SAHARA</div>
            </>
          ) : (
            <span className="pr-help">Schedule complete</span>
          )}
        </div>

        <div className="pr-row-actions">
          {entry.owed > 0n && (canCollect ? (
            <button type="button" className="sh-btn sh-btn-sm sh-btn-primary" disabled={!!busy || paused}
              onClick={() => run(`pay-${entry.id}`, 'Payment sent.', () => distributor.claimEntries([entry.id]))}>
              {canPay ? 'Pay' : 'Collect'} {tokens(entry.owed)}
            </button>
          ) : (
            <span className="pr-chip pr-chip-accent">{tokens(entry.owed)} due</span>
          ))}
          <Link to={href} replace className="sh-btn sh-btn-ghost sh-btn-sm" aria-expanded={open}
            aria-label={open ? 'Hide details' : 'View details'}>
            <span className="pr-chev" style={{ display: 'inline-flex' }}><Icon d={ICONS.chevron} size={14} /></span>
          </Link>
        </div>
      </div>
      {open && (
        <EntryDetails entry={entry} isOwner={isOwner} canCollect={canCollect} canPay={canPay}
          paused={paused} pool={pool} busy={busy} run={run} distributor={distributor} />
      )}
    </article>
  );
};

// ---------------------------------------------------------------------------
// Pager
// ---------------------------------------------------------------------------

/**
 * Page controls for the roster. Changing page or page size goes back to the
 * plain list URL, so a selected entry on another page does not pin the view.
 */
const Pager: React.FC<{ distributor: ReturnType<typeof useDistributor> }> = ({ distributor }) => {
  const navigate = useNavigate();
  const { page, pageCount, pageSize, pageLoading, state } = distributor;
  const total = Number(state?.entryCount ?? 0n);
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const go = (next: number) => {
    navigate('/payroll', { replace: true });
    distributor.setPage(next);
  };
  return (
    <div className="pr-pager">
      <span className="pr-help" aria-live="polite">
        {from.toLocaleString('en-US')}–{to.toLocaleString('en-US')} of {total.toLocaleString('en-US')}
        {pageLoading && <span className="sh-spinner sh-spinner-sm" style={{ display: 'inline-block', marginLeft: 10, verticalAlign: -2 }} />}
      </span>
      <div className="pr-pager-controls">
        <label className="pr-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Per page
          <select className="sh-select pr-pager-size" aria-label="Payments per page" value={pageSize}
            onChange={(e) => {
              navigate('/payroll', { replace: true });
              distributor.setPageSize(Number(e.target.value));
            }}>
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button type="button" className="sh-btn sh-btn-sm" aria-label="Previous page"
          disabled={page === 0} onClick={() => go(page - 1)}>
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon d={ICONS.chevron} size={12} /></span>
        </button>
        <span className="pr-exact">{page + 1} / {pageCount}</span>
        <button type="button" className="sh-btn sh-btn-sm" aria-label="Next page"
          disabled={page + 1 >= pageCount} onClick={() => go(page + 1)}>
          <Icon d={ICONS.chevron} size={12} />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

type Tab = 'payments' | 'pool' | 'keys';
const TAB_LABELS: Record<Tab, string> = {
  payments: 'Payments',
  pool: 'Pool',
  keys: 'Keys & controls',
};

export const PayrollScreen: React.FC = () => {
  const { id: selectedId } = useParams<{ id: string }>();
  // Ids are roster indices; anything that is not one simply selects nothing.
  const selected = selectedId !== undefined && /^\d+$/.test(selectedId) ? BigInt(selectedId) : null;
  const distributor = useDistributor({ selectedId: selected });
  const { busy, run } = useAction();

  const [creating, setCreating] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawTo, setWithdrawTo] = useState('');
  const [ethTo, setEthTo] = useState('');
  const [claimerAddress, setClaimerAddress] = useState('');
  const [newOwner, setNewOwner] = useState('');

  const { state, entries, isOwner, isPendingOwner, canPay } = distributor;

  // The screen holds ONE PAGE of the roster, never all of it, so anything about
  // the whole roster comes from the contract's own aggregates -- totalOwed here
  // -- and never from summing `entries`.
  const dueTotal = state?.totalOwed ?? 0n;

  // ------------------------------------------------------------ tabs
  // Declared before the early return below: these are hooks, and hooks must run
  // on every render.
  // Which tabs this wallet gets: funding needs a wallet to sign with, and the
  // keys belong to the owner alone. The tab lives in the URL (?tab=pool), so it
  // can be linked and the back button returns to the previous one.
  const [params, setParams] = useSearchParams();
  const tabs: Tab[] = ['payments'];
  if (distributor.account || isOwner) tabs.push('pool');
  if (isOwner) tabs.push('keys');
  const requested = params.get('tab') as Tab | null;
  // An expanded payment lives on the Payments tab, whatever the URL asks for.
  const tab: Tab = selected !== null ? 'payments'
    : requested && tabs.includes(requested) ? requested : 'payments';
  const tabsAnchor = useRef<HTMLDivElement>(null);
  const focusFund = useRef(false);
  const selectTab = (next: Tab) => {
    setParams(next === 'payments' ? {} : { tab: next });
    // Switching from deep inside a long list would otherwise leave the window
    // scrolled past the end of a shorter panel. Only when the bar is stuck:
    // a tab click near the top of the page must not jump.
    const top = tabsAnchor.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) window.scrollTo({ top: window.scrollY + top - 8 });
  };
  useEffect(() => {
    if (tab !== 'pool' || !focusFund.current) return;
    focusFund.current = false;
    document.getElementById('payroll-fund')?.focus();
  }, [tab]);

  if (distributor.address === null) {
    return (
      <div className="sh-main pr-page">
        <div className="sh-main-inner" style={{ maxWidth: 720 }}>
          <div className="sh-label">Monthly payouts</div>
          <h1 className="sh-h1">
            Not deployed on{' '}
            <span className="sh-serif" style={{ color: 'var(--accent)' }}>{environment.chainName}</span>
          </h1>
          <p className="sh-sub" style={{ marginTop: 14 }}>
            The recurring payout contract has not been deployed to this chain yet. Deploy it with{' '}
            <span className="sh-mono">npx hardhat ignition deploy ignition/modules/monthlyDistributor.ts</span>
            , then set <span className="sh-mono">distributorAddress</span> for this chain in{' '}
            <span className="sh-mono">dapp/src/config/environment.ts</span>.
          </p>
          <p style={{ marginTop: 24 }}>
            <Link to="/" className="sh-btn">Back to allocation</Link>
          </p>
        </div>
      </div>
    );
  }

  // How much of the next run the pool already covers. fundingRequiredNextRun is
  // the SHORTFALL (clamped at zero), so the run's full cost is the shortfall
  // plus whatever part of the pool it would consume.
  const need = state?.fundingRequiredNextRun ?? 0n;
  const pool = state?.poolBalance ?? 0n;
  const funded = need === 0n;
  const coverage = funded ? 1 : Number((pool * 1000n) / (pool + need)) / 1000;
  const contractHref = explorerAddress(distributor.address);

  // Top up opens the Pool tab with the next run's shortfall already filled in
  // and the cursor in the field -- one click from the hero to signing.
  const scrollToFund = () => {
    if (need > 0n) setFundAmount(formatUnits(need, environment.tokenDecimals));
    focusFund.current = true;
    selectTab('pool');
  };

  return (
    <div className="sh-main pr-page">
      <div className="sh-main-inner" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <header className="pr-header">
          <div>
            <div className="sh-label">Operations</div>
            <h1 className="sh-h1">
              Monthly <span className="sh-serif" style={{ color: 'var(--text-2)' }}>payouts</span>
            </h1>
            <p className="sh-sub" style={{ marginTop: 12 }}>
              Paid on the 26th of every month at 12:00 UTC — 20:00 in Shanghai. A missed run costs
              nothing: the next one pays everything outstanding.
            </p>
          </div>
          <div className="pr-header-actions">
            <button type="button" className="sh-btn" disabled={distributor.isLoading}
              onClick={() => void distributor.refresh()}>
              {distributor.isLoading ? <span className="sh-spinner sh-spinner-sm" /> : null}
              Refresh
            </button>
            {isOwner && !creating && (
              <button type="button" className="sh-btn sh-btn-primary" onClick={() => setCreating(true)}>
                <Icon d={ICONS.plus} size={14} />
                Add a payment
              </button>
            )}
          </div>
        </header>

        {/* Which contract this screen is driving, stated up front: the address
            is what an operator checks against a deployment record or a
            multisig transaction before signing anything, so it belongs next to
            the title rather than in a footnote. */}
        <section className="pr-contract" aria-label="Contract">
          <div className="pr-contract-main">
            <span className="sh-label">Contract</span>
            <span className="pr-net"><span className="sh-dot" />{environment.chainName}</span>
            <span className="pr-contract-addr" title={distributor.address}>{distributor.address}</span>
            <span className="pr-contract-actions">
              <button type="button" className="sh-btn sh-btn-sm" onClick={() => void copy(distributor.address!)}>
                <Icon d={ICONS.copy} size={12} />
                Copy
              </button>
              {contractHref && (
                <a className="sh-btn sh-btn-sm" href={contractHref} target="_blank" rel="noreferrer">
                  {EXPLORER_NAMES[environment.chainId] ?? 'Explorer'}
                  <Icon d={ICONS.arrowUpRight} size={12} />
                </a>
              )}
            </span>
          </div>
          {state && (
            <div className="pr-contract-token">
              <span className="sh-label">Pays in</span>
              <AddressLine address={state.token} />
            </div>
          )}
        </section>

        {isOwner && creating && (
          <CreateEntryForm
            busy={busy === 'create'}
            onClose={() => setCreating(false)}
            onCreate={(input) => run('create', 'Payment created.', async () => {
              await distributor.createEntry(input);
              setCreating(false);
            })}
          />
        )}

        {distributor.error && (
          <div role="alert" className="sh-banner sh-banner-danger" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <div>Unable to read the payout contract. {distributor.error}</div>
            <button type="button" className="sh-btn sh-btn-sm" onClick={() => void distributor.refresh()}>Retry</button>
          </div>
        )}

        {!state ? (
          distributor.error ? null : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
              <Spinner label="Reading payout contract" />
            </div>
          )
        ) : (
          <>
            {state.paused && (
              <div role="alert" className="sh-banner sh-banner-warn">
                <Icon d={ICONS.warn} size={16} />
                <div>
                  <strong>Payouts are paused.</strong> Entitlement keeps accruing and nothing is
                  forfeited; no payment can be collected until the owner resumes.
                </div>
              </div>
            )}

            {isPendingOwner && (
              <div role="alert" className="sh-banner sh-banner-warn" style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>
                  This wallet is the pending owner. Everything below still belongs to the current
                  owner until you accept.
                </div>
                <button type="button" className="sh-btn sh-btn-sm sh-btn-primary" disabled={!!busy}
                  onClick={() => run('accept', 'Ownership accepted.', () => distributor.acceptOwnership())}>
                  Accept ownership
                </button>
              </div>
            )}

            {/* ---------------- Hero: the next run, and whether it is funded ---------------- */}
            <section className="pr-tiles pr-hero" aria-label="Payout summary">
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div className="sh-label">Next payout</div>
                  <span className={`pr-chip${dueTotal > 0n ? ' pr-chip-accent' : ''}`}>
                    <Icon d={ICONS.clock} size={12} />
                    {relative(state.nextPayoutInstant)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 22, flexWrap: 'wrap' }}>
                  <span className="pr-hero-num">
                    {new Date(Number(state.nextPayoutInstant) * 1000).getUTCDate()}
                  </span>
                  <span className="pr-hero-unit">{monthYear(state.nextPayoutInstant)}</span>
                </div>
                <div className="pr-exact" style={{ marginTop: 14 }}>
                  {date(state.nextPayoutInstant)} · {shanghai(state.nextPayoutInstant)}
                </div>

                <div style={{ marginTop: 28, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* A disabled yellow button reads as a broken control, not as "all
                      settled", so with nothing due the payer gets the same calm chip
                      everyone else does. */}
                  {canPay && dueTotal > 0n ? (
                    // Finds who is owed on click (pendingInRange, in chunks) rather
                    // than on load, so a large roster costs nothing until paid.
                    <button type="button" className="sh-btn sh-btn-primary sh-btn-lg"
                      disabled={!!busy || state.paused}
                      onClick={() => run('payAll', '', async () => {
                        const { paid, transactions } = await distributor.claimAllDue();
                        toast.success(transactions > 1
                          ? `Paid ${paid} payments in ${transactions} transactions.`
                          : `Paid ${paid} payment${paid === 1 ? '' : 's'}.`);
                      })}>
                      {busy === 'payAll' ? 'Paying…' : `Pay everyone due · ${tokens(dueTotal)}`}
                    </button>
                  ) : (
                    <span className={`pr-chip${dueTotal > 0n ? ' pr-chip-accent' : ''}`}>
                      {dueTotal > 0n ? `${tokens(dueTotal)} due now` : (
                        <><Icon d={ICONS.check} size={12} />Nothing due right now</>
                      )}
                    </span>
                  )}
                  {dueTotal === 0n && canPay && (
                    <span className="pr-help">Everything that has come due has been paid.</span>
                  )}
                </div>
                {dueTotal > state.poolBalance && (
                  <p className="pr-help" style={{ marginTop: 12, color: 'var(--warn)' }}>
                    The pool holds {tokens(state.poolBalance)}; paying now settles what it covers and
                    leaves the rest recorded as still owed.
                  </p>
                )}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div className="sh-label">Pool</div>
                  {funded ? (
                    <span className="sh-pill sh-pill-success">Funded for the 26th</span>
                  ) : (
                    <span className="sh-pill sh-pill-warn">Short {amount(need)}</span>
                  )}
                </div>
                <div className="pr-big" title={exact(state.poolBalance)} style={{ marginTop: 26 }}>
                  {amount(state.poolBalance)}<span className="pr-unit">SAHARA</span>
                </div>
                <div className={`pr-meter${funded ? '' : ' is-short'}`} style={{ marginTop: 18 }}>
                  <span style={{ width: `${Math.round(coverage * 100)}%` }} />
                </div>
                <p className="pr-help" style={{ marginTop: 12 }}>
                  {funded ? (
                    <>Covers the next run in full, plus anything already overdue.</>
                  ) : (
                    <>
                      To fund before then: <strong>{tokens(state.fundingRequiredNextRun)}</strong>, which
                      covers the next run plus anything already overdue.
                    </>
                  )}
                </p>
                {/* Only where the funding form it jumps to exists: without a wallet
                    that form is hidden, and this would be a dead button. */}
                {(distributor.account || isOwner) && (
                  <button type="button" className="sh-btn sh-btn-sm" style={{ marginTop: 16 }} onClick={scrollToFund}>
                    <Icon d={ICONS.plus} size={12} />
                    Top up
                  </button>
                )}
              </div>
            </section>

            <section className="pr-tiles pr-stats" aria-label="Totals">
              <Stat label="Owed right now" value={state.totalOwed}
                tone={state.totalOwed > 0n ? 'var(--accent)' : undefined} />
              <Stat label="Short right now" value={state.fundingRequired}
                tone={state.fundingRequired > 0n ? 'var(--danger)' : undefined}
                hint={state.fundingRequired > 0n ? 'Someone is unpaid today.' : undefined} />
              {/* Not "paid to date": the contract keeps no such total, and summing
                  it would mean reading every entry -- exactly what paging avoids. */}
              <div>
                <div className="sh-label">Payments</div>
                <div className="pr-stat-value">
                  {state.entryCount.toLocaleString('en-US')}
                  <span className="pr-unit">{state.entryCount === 1n ? 'entry' : 'entries'}</span>
                </div>
              </div>
              <Stat label="Committed, unpaid" value={state.totalRemainingObligation} />
            </section>

            {/* ---------------- Tabs ---------------- */}
            {/* The page's work lives in three places -- the roster, the pool, and
                the keys -- and stacking them made the last two a long scroll
                below a long list. Tabs put all three one click apart, and the
                bar sticks to the top of the window, so it is reachable from
                anywhere in a 50-row page. */}
            <div ref={tabsAnchor} aria-hidden="true" />
            {tabs.length > 1 && (
              <nav className="pr-tabs" role="tablist" aria-label="Payout sections">
                {tabs.map((t) => (
                  <button key={t} type="button" role="tab" id={`pr-tab-${t}`}
                    aria-selected={tab === t} aria-controls={`pr-panel-${t}`}
                    className={`pr-tab${tab === t ? ' is-on' : ''}`}
                    onClick={() => selectTab(t)}>
                    {TAB_LABELS[t]}
                    {t === 'payments' && <span className="pr-tab-count">{state.entryCount.toLocaleString('en-US')}</span>}
                    {t === 'pool' && (
                      <span className={`sh-dot${funded ? '' : ' sh-dot-warn'}`}
                        title={funded ? 'Funded for the next run' : `Short ${tokens(need)}`} />
                    )}
                    {t === 'keys' && <span className="pr-tab-count">{state.claimers.length}</span>}
                  </button>
                ))}
              </nav>
            )}

            {tab === 'payments' && (
              <section id="pr-panel-payments" role="tabpanel" aria-labelledby="pr-tab-payments" aria-label="Payments">
                {tabs.length === 1 && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
                    <h2 className="sh-h2">Payments</h2>
                    <span className="pr-chip" style={{ padding: '2px 9px', fontSize: 11.5 }}>
                      {state.entryCount.toLocaleString('en-US')}
                    </span>
                  </div>
                )}
              {state.entryCount === 0n ? (
                <div className="pr-list pr-empty">
                  <div className="sh-serif" style={{ fontSize: 26, color: 'var(--text-2)' }}>No payments yet</div>
                  <p className="pr-help" style={{ marginTop: 10 }}>
                    {isOwner ? 'Use “Add a payment” above to create the first one.' : 'The owner has not created any payments.'}
                  </p>
                </div>
              ) : (
                <div className="pr-list">
                  <div className="pr-list-head">
                    <span className="sh-label">Payment</span>
                    <span className="sh-label">Progress</span>
                    <span className="sh-label">Next</span>
                    <span />
                  </div>
                  {distributor.pageError && (
                    <div role="alert" className="sh-banner sh-banner-danger" style={{ margin: 16, alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>{distributor.pageError}</div>
                      <button type="button" className="sh-btn sh-btn-sm" onClick={distributor.retryPage}>Retry</button>
                    </div>
                  )}
                  {entries.length === 0 && distributor.pageLoading && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '44px 0' }}>
                      <Spinner label="Loading payments" />
                    </div>
                  )}
                  {entries.map((entry) => (
                    <PaymentRow
                      key={entry.id.toString()}
                      entry={entry}
                      open={selected === entry.id}
                      canPay={canPay}
                      paused={state.paused}
                      busy={busy}
                      run={run}
                      distributor={distributor}
                      isOwner={isOwner}
                      account={distributor.account}
                      pool={state.poolBalance}
                    />
                  ))}
                  <Pager distributor={distributor} />
                </div>
              )}
              </section>
            )}

            {tab === 'pool' && (
              <section id="pr-panel-pool" role="tabpanel" aria-labelledby="pr-tab-pool" aria-label="Pool" className="pr-ops">
                <div className="pr-card" style={isOwner ? undefined : { gridColumn: '1 / -1' }}>
                <h3 className="pr-card-title">Fund the pool</h3>
                <p className="pr-help">
                  Every payment comes out of one shared balance. Anyone may top it up; only the owner
                  can sweep it.
                </p>
                <div className="pr-inline" style={{ marginTop: 16 }}>
                  <div className="pr-suffix">
                    <input id="payroll-fund" type="text" inputMode="decimal" className="sh-input"
                      aria-label="Amount to add to the pool" value={fundAmount}
                      onChange={(e) => setFundAmount(e.target.value)}
                      placeholder={amount(state.fundingRequiredNextRun)} />
                    <span>SAHARA</span>
                  </div>
                  <button type="button" className="sh-btn sh-btn-primary sh-btn-sm"
                    disabled={!!busy || fundAmount.trim() === ''}
                    onClick={() => run('fund', 'Pool funded.', async () => {
                      await distributor.fundPool(parseUnits(fundAmount.trim(), environment.tokenDecimals));
                      setFundAmount('');
                    })}>
                    {busy === 'fund' ? 'Funding…' : 'Fund'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                  <span className="pr-help">This wallet holds {tokens(state.walletBalance)}.</span>
                  {state.fundingRequiredNextRun > 0n && (
                    <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm"
                      onClick={() => setFundAmount(formatUnits(state.fundingRequiredNextRun, environment.tokenDecimals))}>
                      Use {tokens(state.fundingRequiredNextRun)}
                    </button>
                  )}
                </div>
                </div>
                {isOwner && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="pr-card">
                  <h3 className="pr-card-title">Sweep from the pool</h3>
                    
                    <p className="pr-help">
                      Unrestricted: this can take the whole balance, including amounts recipients have
                      earned and not yet collected. The debt stays recorded and visible.
                    </p>
                    <div className="pr-inline">
                      <input type="text" className="sh-input sh-mono" placeholder="Destination 0x…"
                        aria-label="Sweep destination" value={withdrawTo} onChange={(e) => setWithdrawTo(e.target.value)} />
                    </div>
                    <div className="pr-inline">
                      <div className="pr-suffix">
                        <input type="text" inputMode="decimal" className="sh-input" placeholder="Amount"
                          aria-label="Amount to sweep" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} />
                        <span>SAHARA</span>
                      </div>
                      <button type="button" className="sh-btn sh-btn-sm sh-btn-danger"
                        disabled={!!busy || !isAddress(withdrawTo.trim()) || withdrawAmount.trim() === ''}
                        onClick={() => run('withdraw', 'Swept.', async () => {
                          await distributor.withdraw(withdrawTo.trim(), parseUnits(withdrawAmount.trim(), environment.tokenDecimals));
                          setWithdrawAmount('');
                          setWithdrawTo('');
                        })}>
                        Sweep
                      </button>
                    </div>
                </div>
                    {/* Shown only when there is something to recover. The contract
                        refuses plain ETH transfers, so a balance here arrived without
                        asking -- a selfdestruct or a block reward -- and is rare. */}
                    {state.ethBalance > 0n && (
                <div className="pr-card">
                  <h3 className="pr-card-title">Recover ETH</h3>
                    
                    <p className="pr-help">
                      The contract holds <strong title={`${formatUnits(state.ethBalance, 18)} ETH`}>{display(state.ethBalance, 18)} ETH</strong>. It never
                      accepts ETH on purpose, so this arrived by force and can be sent anywhere.
                    </p>
                    <div className="pr-inline">
                      <input type="text" className="sh-input sh-mono" placeholder="Destination 0x…"
                        aria-label="ETH destination" value={ethTo} onChange={(e) => setEthTo(e.target.value)} />
                      <button type="button" className="sh-btn sh-btn-sm sh-btn-danger"
                        disabled={!!busy || !isAddress(ethTo.trim())}
                        onClick={() => run('withdrawEth', 'ETH recovered.', async () => {
                          await distributor.withdrawETH(ethTo.trim(), state.ethBalance);
                          setEthTo('');
                        })}>
                        Recover all ETH
                      </button>
                    </div>
                </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {tab === 'keys' && isOwner && (
              <section id="pr-panel-keys" role="tabpanel" aria-labelledby="pr-tab-keys" aria-label="Keys and controls" className="pr-ops">
                <div className="pr-card">
                  <h3 className="pr-card-title">Claimer keys</h3>
                    
                    <p className="pr-help">
                      May trigger a payout for anyone, and nothing else. Payment always goes to the
                      recorded recipient, so this key is safe to leave on the server that runs the
                      monthly job.
                    </p>
                    <div style={{ marginTop: 12 }}>
                      {state.claimers.length === 0 ? (
                        <p className="pr-help">None appointed.</p>
                      ) : state.claimers.map((claimer) => (
                        <div key={claimer} className="pr-key">
                          <span title={claimer}>{shortAddress(claimer)}</span>
                          <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" disabled={!!busy}
                            onClick={() => run(`revoke-${claimer}`, 'Claimer removed.', () => distributor.setClaimer(claimer, false))}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="pr-inline">
                      <input id="payroll-claimer" type="text" className="sh-input sh-mono" placeholder="0x…"
                        aria-label="Claimer address" value={claimerAddress} onChange={(e) => setClaimerAddress(e.target.value)} />
                      <button type="button" className="sh-btn sh-btn-sm" disabled={!!busy || !isAddress(claimerAddress.trim())}
                        onClick={() => run('addClaimer', 'Claimer appointed.', async () => {
                          await distributor.setClaimer(claimerAddress.trim(), true);
                          setClaimerAddress('');
                        })}>
                        Appoint claimer
                      </button>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="pr-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div>
                        <h3 className="pr-card-title">{state.paused ? 'Payouts paused' : 'Payouts running'}</h3>
                        <p className="pr-help">
                          Pausing stops every payout while the schedules keep running. Nothing is forfeited.
                        </p>
                      </div>
                      <button type="button"
                        className={`sh-btn sh-btn-sm ${state.paused ? 'sh-btn-success' : 'sh-btn-danger'}`}
                        disabled={!!busy}
                        onClick={() => run('pause', state.paused ? 'Payouts resumed.' : 'Payouts paused.',
                          () => distributor.setPaused(!state.paused))}>
                        {state.paused ? 'Resume payouts' : 'Pause payouts'}
                      </button>
                    </div>
                  </div>
                <div className="pr-card">
                  <h3 className="pr-card-title">Transfer ownership</h3>
                    
                    <p className="pr-help">
                      Two-step: this only records a pending owner. Nothing changes until that wallet
                      accepts.
                      {state.pendingOwner && <> Pending: <span className="sh-mono">{shortAddress(state.pendingOwner)}</span></>}
                    </p>
                    <div className="pr-inline">
                      <input id="payroll-owner" type="text" className="sh-input sh-mono" placeholder="0x…"
                        aria-label="New owner" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} />
                      <button type="button" className="sh-btn sh-btn-sm" disabled={!!busy || !isAddress(newOwner.trim())}
                        onClick={() => run('transfer', 'Handover started.', async () => {
                          await distributor.transferOwnership(newOwner.trim());
                          setNewOwner('');
                        })}>
                        Start handover
                      </button>
                    </div>
                </div>
                </div>
              </section>
            )}

            <footer className="pr-help">
              All dates are in UTC, with the Shanghai time alongside. Amounts are shown to two
              decimals; hover a figure for the exact value.
            </footer>
          </>
        )}
      </div>
    </div>
  );
};
